import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { CallState, VideoCallService } from '../../services/video-call.service';

const WIDGET_WIDTH = 168;
const WIDGET_HEIGHT = 220;
const EDGE_MARGIN = 14;
const DRAG_THRESHOLD = 6;
const PIP_WIDTH = 340;
const PIP_HEIGHT = 260;

const ICON_MIC = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"/></svg>';
const ICON_CAM = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z"/></svg>';
const ICON_EXPAND = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M9 3H3v6h2V5h4V3zm12 0h-6v2h4v4h2V3zM5 15H3v6h6v-2H5v-4zm14 4h-4v2h6v-6h-2v4z"/></svg>';
const ICON_END = '<svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.996.996 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .27-.11.52-.29.7l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.73-1.68-1.36-2.66-1.85a.996.996 0 0 1-.56-.9v-3.1A15.94 15.94 0 0 0 12 9z"/></svg>';

export interface BgPreset {
  id: string;
  label: string;
  colors: [string, string];
}

const BG_PRESETS: BgPreset[] = [
  { id: 'sky', label: 'Sky', colors: ['#4facfe', '#00f2fe'] },
  { id: 'sage', label: 'Sage', colors: ['#43e97b', '#38f9d7'] },
  { id: 'sunset', label: 'Sunset', colors: ['#fa709a', '#fee140'] }
];

const PIP_STYLES = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: #0b0f19; overflow: hidden;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
  .pip-stage { position: relative; width: 100%; height: 100%;
    background: radial-gradient(120% 120% at 50% 0%, #182238 0%, #0b0f19 60%); }
  .pip-remote { position: absolute !important; inset: 0; width: 100% !important; height: 100% !important; background: transparent; }
  .pip-remote video { width: 100% !important; height: 100% !important; object-fit: cover; }
  .pip-placeholder { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: 10px; text-align: center; padding: 14px; }
  .pip-avatar { width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    font-size: 20px; font-weight: 600; color: #fff; background: linear-gradient(135deg, #3880ff, #6f5bff); }
  .pip-status { margin: 0; color: rgba(255,255,255,.85); font-size: 12px; max-width: 280px; }
  .pip-local { position: absolute !important; right: 10px; bottom: 54px; width: 72px !important; height: 96px !important;
    border-radius: 10px; overflow: hidden; background: #1a2233; border: 2px solid rgba(255,255,255,.14);
    box-shadow: 0 4px 12px rgba(0,0,0,.4); z-index: 3; }
  .pip-local video { width: 100% !important; height: 100% !important; object-fit: cover; }
  .pip-info { position: absolute; top: 8px; left: 10px; right: 10px; display: flex; align-items: center; gap: 6px;
    color: #fff; font-size: 12px; font-weight: 600; text-shadow: 0 1px 3px rgba(0,0,0,.6); z-index: 4; }
  .pip-dot { width: 7px; height: 7px; border-radius: 50%; background: #ff5252; flex-shrink: 0; }
  .pip-dot.on { background: #34d47a; }
  .pip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pip-timer { margin-left: auto; opacity: .8; font-variant-numeric: tabular-nums; }
  .pip-controls { position: absolute; left: 0; right: 0; bottom: 10px; display: flex; align-items: center;
    justify-content: center; gap: 10px; z-index: 4; }
  .pip-btn { position: relative; width: 34px; height: 34px; border-radius: 50%; border: none; padding: 0;
    display: inline-flex; align-items: center; justify-content: center; background: rgba(255,255,255,.16);
    color: #fff; cursor: pointer; }
  .pip-btn.active { background: #fff; color: #0b0f19; }
  .pip-btn.active::after { content: ''; position: absolute; width: 2px; height: 68%; background: #ff3b30;
    transform: rotate(45deg); border-radius: 2px; }
  .pip-btn.pip-end { background: #ff3b30; }
`;

@Component({
  selector: 'app-floating-call',
  templateUrl: './floating-call.component.html',
  styleUrls: ['./floating-call.component.scss']
})
export class FloatingCallComponent implements OnInit, OnDestroy {

  cs: CallState = {
    active: false,
    minimized: false,
    connecting: false,
    videoConnected: false,
    callEndedByRemote: false,
    doctorName: '',
    isMuted: false,
    isVideoMuted: false,
    elapsed: 0,
    routeId: null,
    virtualBackground: 'none',
    virtualBackgroundImage: null,
    virtualBackgroundBusy: false
  };

  posX: number | null = null;
  posY: number | null = null;

  /** true while the call video is living inside a real OS-level Picture-in-Picture window */
  inSystemPip = false;

  readonly bgPresets = BG_PRESETS;
  bgPanelOpen = false;
  selectedPresetId: string | null = null;
  hasCustomBg = false;

  @ViewChild('bgFileInput') bgFileInputRef: ElementRef<HTMLInputElement>;

  private customBgUrl: string | null = null;
  private dragging = false;
  private dragMoved = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private sub: Subscription;
  private returnSub: Subscription;

  private pipWindow: any = null;
  private originalRemoteParent: HTMLElement | null = null;
  private originalLocalParent: HTMLElement | null = null;
  private pipRefs: {
    timer: HTMLElement; name: HTMLElement; dot: HTMLElement;
    status: HTMLElement; placeholder: HTMLElement;
    muteBtn: HTMLElement; videoBtn: HTMLElement;
  } | null = null;

  private readonly onPipHide = () => this.handlePipClosed();

  constructor(
    private videoCallService: VideoCallService,
    private el: ElementRef<HTMLElement>,
    private zone: NgZone) { }

  ngOnInit() {
    this.sub = this.videoCallService.state$.subscribe(state => {
      if (!state.active && this.pipWindow) {
        this.exitSystemPip(false);
      }

      const wasMinimized = this.cs.minimized;
      this.cs = state;

      if (state.minimized && !wasMinimized && !this.inSystemPip) {
        this.placeWidgetDefault();
      }
      if (!state.active || state.minimized) {
        this.bgPanelOpen = false;
      }
      if (state.active) {
        setTimeout(() => this.registerContainersIfNeeded());
      }
      if (this.inSystemPip) {
        this.updatePipContent();
      }
    });

    this.returnSub = this.videoCallService.returnRequested$.subscribe(() => {
      if (this.inSystemPip) {
        this.exitSystemPip(true);
      } else {
        this.videoCallService.restore();
      }
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.returnSub?.unsubscribe();
    if (this.pipWindow) {
      try { this.pipWindow.close(); } catch { }
    }
    if (this.customBgUrl) {
      URL.revokeObjectURL(this.customBgUrl);
    }
  }

  private registerContainersIfNeeded() {
    const remoteEl = this.el.nativeElement.querySelector('#remote-container') as HTMLElement | null;
    const localEl = this.el.nativeElement.querySelector('#local-container') as HTMLElement | null;
    if (remoteEl && localEl) {
      this.videoCallService.registerContainers(remoteEl, localEl);
    }
  }

  get initials(): string {
    const name = (this.cs.doctorName || '').trim();
    if (!name) { return 'DR'; }
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(p => p[0].toUpperCase())
      .join('');
  }

  get statusText(): string {
    if (this.cs.callEndedByRemote) { return 'Call ended'; }
    if (this.cs.connecting) { return 'Connecting…'; }
    if (!this.cs.videoConnected) { return `Waiting for ${this.cs.doctorName || 'doctor'} to join`; }
    return '';
  }

  get formattedElapsed(): string {
    const total = this.cs.elapsed || 0;
    const m = Math.floor(total / 60).toString().padStart(2, '0');
    const s = (total % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  get pipSupported(): boolean {
    return typeof window !== 'undefined' && !!(window as any).documentPictureInPicture;
  }

  get widgetStyle(): { [key: string]: string } {
    if (this.inSystemPip || !this.cs.minimized || this.posX === null || this.posY === null) {
      return {};
    }
    return {
      left: `${this.posX}px`,
      top: `${this.posY}px`,
      right: 'auto',
      bottom: 'auto'
    };
  }

  private placeWidgetDefault() {
    this.posX = Math.max(EDGE_MARGIN, window.innerWidth - WIDGET_WIDTH - EDGE_MARGIN);
    this.posY = Math.max(EDGE_MARGIN, window.innerHeight - WIDGET_HEIGHT - 96);
  }

  async minimize(ev?: Event) {
    ev?.stopPropagation();
    const poppedOut = await this.tryEnterSystemPip();
    if (!poppedOut) {
      this.videoCallService.minimize();
    }
  }

  expand(ev?: Event) {
    ev?.stopPropagation();
    if (!this.inSystemPip && this.dragMoved) { return; }
    this.videoCallService.expand();
  }

  onStageClick(ev: Event) {
    if (this.bgPanelOpen) {
      this.bgPanelOpen = false;
      return;
    }
    if (this.cs.minimized && !this.inSystemPip) {
      this.expand(ev);
    }
  }

  hangup(ev?: Event) {
    ev?.stopPropagation();
    this.videoCallService.hangup();
  }

  toggleMute(ev?: Event) {
    ev?.stopPropagation();
    this.videoCallService.toggleMute();
  }

  toggleVideo(ev?: Event) {
    ev?.stopPropagation();
    this.videoCallService.toggleVideo();
  }

  switchCamera(ev?: Event) {
    ev?.stopPropagation();
    this.videoCallService.switchCamera();
  }

  get vbSupported(): boolean {
    return this.videoCallService.virtualBackgroundSupported;
  }

  toggleBgPanel(ev?: Event) {
    ev?.stopPropagation();
    this.bgPanelOpen = !this.bgPanelOpen;
  }

  selectNoBackground(ev?: Event) {
    ev?.stopPropagation();
    this.selectedPresetId = null;
    this.hasCustomBg = false;
    this.videoCallService.clearVirtualBackground();
  }

  selectBlur(ev?: Event) {
    ev?.stopPropagation();
    this.selectedPresetId = null;
    this.hasCustomBg = false;
    this.videoCallService.setVirtualBackgroundBlur(2);
  }

  selectPreset(preset: BgPreset, ev?: Event) {
    ev?.stopPropagation();
    this.selectedPresetId = preset.id;
    this.hasCustomBg = false;
    const dataUrl = this.renderGradientImage(preset.colors);
    this.videoCallService.setVirtualBackgroundImage(dataUrl);
  }

  triggerUpload(ev?: Event) {
    ev?.stopPropagation();
    this.bgFileInputRef?.nativeElement.click();
  }

  onBgFileSelected(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) { return; }

    if (this.customBgUrl) {
      URL.revokeObjectURL(this.customBgUrl);
    }
    this.customBgUrl = URL.createObjectURL(file);
    this.selectedPresetId = null;
    this.hasCustomBg = true;
    this.videoCallService.setVirtualBackgroundImage(this.customBgUrl);
    input.value = '';
  }

  private renderGradientImage(colors: [string, string]): string {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, colors[0]);
    gradient.addColorStop(1, colors[1]);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  }

  onDragStart(ev: MouseEvent | TouchEvent) {
    if (this.inSystemPip || !this.cs.minimized || this.posX === null || this.posY === null) { return; }
    this.dragging = true;
    this.dragMoved = false;
    const point = this.getPoint(ev);
    this.dragOffsetX = point.x - this.posX;
    this.dragOffsetY = point.y - this.posY;
  }

  @HostListener('window:mousemove', ['$event'])
  @HostListener('window:touchmove', ['$event'])
  onDragMove(ev: MouseEvent | TouchEvent) {
    if (!this.dragging) { return; }
    const point = this.getPoint(ev);
    const nextX = point.x - this.dragOffsetX;
    const nextY = point.y - this.dragOffsetY;

    if (Math.abs(nextX - (this.posX ?? 0)) > DRAG_THRESHOLD || Math.abs(nextY - (this.posY ?? 0)) > DRAG_THRESHOLD) {
      this.dragMoved = true;
    }

    this.posX = this.clamp(nextX, EDGE_MARGIN, window.innerWidth - WIDGET_WIDTH - EDGE_MARGIN);
    this.posY = this.clamp(nextY, EDGE_MARGIN, window.innerHeight - WIDGET_HEIGHT - EDGE_MARGIN);
  }

  @HostListener('window:mouseup')
  @HostListener('window:touchend')
  onDragEnd() {
    this.dragging = false;
  }

  private getPoint(ev: MouseEvent | TouchEvent): { x: number; y: number } {
    if ('touches' in ev && ev.touches.length) {
      return { x: ev.touches[0].clientX, y: ev.touches[0].clientY };
    }
    const mouseEv = ev as MouseEvent;
    return { x: mouseEv.clientX, y: mouseEv.clientY };
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), Math.max(min, max));
  }

  // ---------------------------------------------------------------------
  // System (OS-level) Picture-in-Picture.
  //
  // We deliberately do NOT move the Angular-rendered `.call-shell` across
  // documents — Angular's renderer keeps creating/patching nodes against the
  // original document, so structural directives inside a relocated subtree
  // (and CSS scoped to that document) break once the node comes back.
  // Instead we relocate only the two plain, Agora-owned video container divs
  // (which Angular never mutates internally) and hand-build a small, fully
  // self-contained UI around them directly in the PiP window's document.
  // ---------------------------------------------------------------------

  private async tryEnterSystemPip(): Promise<boolean> {
    const docPip: any = (window as any).documentPictureInPicture;
    if (!docPip || this.pipWindow) { return false; }

    const remoteEl = this.el.nativeElement.querySelector('#remote-container') as HTMLElement | null;
    const localEl = this.el.nativeElement.querySelector('#local-container') as HTMLElement | null;
    if (!remoteEl || !localEl) { return false; }

    try {
      const pipWindow = await docPip.requestWindow({ width: PIP_WIDTH, height: PIP_HEIGHT });
      const doc: Document = pipWindow.document;
      doc.title = this.cs.doctorName ? `Call · ${this.cs.doctorName}` : 'Video call';

      const style = doc.createElement('style');
      style.textContent = PIP_STYLES;
      doc.head.appendChild(style);
      doc.body.innerHTML = '';

      this.originalRemoteParent = remoteEl.parentElement;
      this.originalLocalParent = localEl.parentElement;

      const stage = doc.createElement('div');
      stage.className = 'pip-stage';

      remoteEl.classList.add('pip-remote');
      stage.appendChild(remoteEl);

      const placeholder = doc.createElement('div');
      placeholder.className = 'pip-placeholder';
      const avatar = doc.createElement('div');
      avatar.className = 'pip-avatar';
      const status = doc.createElement('p');
      status.className = 'pip-status';
      placeholder.append(avatar, status);
      stage.appendChild(placeholder);

      localEl.classList.add('pip-local');
      stage.appendChild(localEl);

      const info = doc.createElement('div');
      info.className = 'pip-info';
      const dot = doc.createElement('span');
      dot.className = 'pip-dot';
      const name = doc.createElement('span');
      name.className = 'pip-name';
      const timer = doc.createElement('span');
      timer.className = 'pip-timer';
      info.append(dot, name, timer);
      stage.appendChild(info);

      const controls = doc.createElement('div');
      controls.className = 'pip-controls';
      const muteBtn = this.createPipButton(doc, 'mute', ICON_MIC, 'Mute');
      const videoBtn = this.createPipButton(doc, 'video', ICON_CAM, 'Camera');
      const expandBtn = this.createPipButton(doc, 'expand', ICON_EXPAND, 'Back to tab');
      const endBtn = this.createPipButton(doc, 'hangup', ICON_END, 'End call', 'pip-end');
      controls.append(muteBtn, videoBtn, expandBtn, endBtn);
      stage.appendChild(controls);

      doc.body.appendChild(stage);

      controls.addEventListener('click', (ev: Event) => {
        const target = (ev.target as HTMLElement).closest('button[data-action]') as HTMLElement | null;
        if (!target) { return; }
        const action = target.getAttribute('data-action');
        this.zone.run(() => {
          if (action === 'mute') { this.videoCallService.toggleMute(); }
          else if (action === 'video') { this.videoCallService.toggleVideo(); }
          else if (action === 'expand') { this.videoCallService.expand(); }
          else if (action === 'hangup') { this.videoCallService.hangup(); }
        });
      });

      pipWindow.addEventListener('pagehide', this.onPipHide);

      this.pipWindow = pipWindow;
      this.pipRefs = { timer, name, dot, status, placeholder, muteBtn, videoBtn };
      this.inSystemPip = true;
      this.updatePipContent();
      this.videoCallService.minimize();
      return true;
    } catch {
      return false;
    }
  }

  private createPipButton(doc: Document, action: string, iconSvg: string, label: string, extraClass = ''): HTMLButtonElement {
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = `pip-btn ${extraClass}`.trim();
    btn.setAttribute('data-action', action);
    btn.setAttribute('aria-label', label);
    btn.title = label;
    btn.innerHTML = iconSvg;
    return btn;
  }

  private updatePipContent() {
    if (!this.pipRefs) { return; }
    const r = this.pipRefs;
    r.name.textContent = this.cs.doctorName || 'Doctor';
    r.timer.textContent = this.cs.videoConnected ? this.formattedElapsed : '';
    r.dot.classList.toggle('on', this.cs.videoConnected);
    r.status.textContent = this.statusText;
    r.placeholder.style.display = this.cs.videoConnected ? 'none' : 'flex';
    r.muteBtn.classList.toggle('active', this.cs.isMuted);
    r.videoBtn.classList.toggle('active', this.cs.isVideoMuted);
  }

  private exitSystemPip(forceRestoreTab: boolean) {
    if (!this.pipWindow) { return; }
    const pw = this.pipWindow;

    const remoteEl = pw.document?.getElementById?.('remote-container') as HTMLElement | null;
    const localEl = pw.document?.getElementById?.('local-container') as HTMLElement | null;

    if (remoteEl) {
      remoteEl.classList.remove('pip-remote');
      (this.originalRemoteParent ?? this.el.nativeElement).appendChild(remoteEl);
    }
    if (localEl) {
      localEl.classList.remove('pip-local');
      (this.originalLocalParent ?? this.el.nativeElement).appendChild(localEl);
    }

    this.pipWindow = null;
    this.pipRefs = null;
    this.inSystemPip = false;

    try { pw.removeEventListener('pagehide', this.onPipHide); } catch { }
    try { pw.close(); } catch { }

    if (forceRestoreTab) {
      this.videoCallService.restore();
    }
  }

  private handlePipClosed() {
    const wasActive = this.videoCallService.value.active;
    this.exitSystemPip(wasActive);
  }
}
